import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Send, Image as ImageIcon, Link as LinkIcon, Trash2, Loader2 } from 'lucide-react';
import './CommentThread.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CommentThread({ taskId, currentUser }) {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [linkUrl, setLinkUrl] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (taskId) {
      fetchComments();
    }
  }, [taskId]);

  const fetchComments = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/tasks/${taskId}/comments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (1MB)
    if (file.size > 1024 * 1024) {
      toast.error('File size exceeds 1MB. Please add as a link instead.');
      return;
    }

    setImageFile(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSendComment = async () => {
    if (!newComment.trim() && !imageFile && !linkUrl) {
      return;
    }

    try {
      setIsSending(true);
      const token = localStorage.getItem('session_token');
      let imageUrl = null;

      // Upload image if present
      if (imageFile) {
        try {
          const formData = new FormData();
          formData.append('file', imageFile);
          
          const uploadResponse = await axios.post(`${API}/comments/upload-image`, formData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          imageUrl = uploadResponse.data.image_url;
        } catch (uploadError) {
          console.error('Image upload failed:', uploadError);
          toast.error(uploadError.response?.data?.detail || 'Failed to upload image');
          setIsSending(false);
          return;
        }
      }

      // Construct comment text with link if present
      let commentText = newComment.trim();
      if (linkUrl) {
        commentText = commentText 
          ? `${commentText}\n\n${linkUrl}` 
          : linkUrl;
      }
      
      // If only image without text, use a placeholder
      if (!commentText && imageUrl) {
        commentText = ' '; // Single space as placeholder for image-only comments
      }

      // Send comment as JSON body
      console.log('Posting comment with text:', commentText, 'imageUrl:', imageUrl ? 'present' : 'none');
      
      const response = await axios.post(
        `${API}/tasks/${taskId}/comments`,
        {
          text: commentText,
          image_url: imageUrl
        },
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      console.log('Comment posted successfully:', response.data);
      toast.success('Comment added');
      
      // Reset form
      setNewComment('');
      setImageFile(null);
      setImagePreview(null);
      setLinkUrl('');
      setShowLinkInput(false);
      
      // Refresh comments
      await fetchComments();
    } catch (error) {
      console.error('Failed to send comment:', error);
      toast.error(error.response?.data?.detail || 'Failed to add comment');
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      const token = localStorage.getItem('session_token');
      await axios.delete(`${API}/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Comment deleted');
      await fetchComments();
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    
    return date.toLocaleDateString();
  };

  const extractLinks = (text) => {
    // Match both http(s):// URLs and www. URLs
    const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = urlRegex.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
      }
      // Add https:// to www links
      const url = match[0].startsWith('www.') ? `https://${match[0]}` : match[0];
      parts.push({ type: 'link', content: match[0], url: url });
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.substring(lastIndex) });
    }

    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  return (
    <div className="comment-thread">
      <div className="comment-thread-header">
        <h3>Comments</h3>
        <span className="comment-count">{comments.length}</span>
      </div>

      <div className="comments-list">
        {isLoading ? (
          <div className="comments-loading">
            <Loader2 className="animate-spin" size={24} />
          </div>
        ) : comments.length === 0 ? (
          <div className="no-comments">
            <p>No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="comment-item">
              <Avatar className="comment-avatar">
                <AvatarImage src={comment.user_picture} alt={comment.user_name} />
                <AvatarFallback>{comment.user_name?.[0] || 'U'}</AvatarFallback>
              </Avatar>
              
              <div className="comment-content">
                <div className="comment-header">
                  <span className="comment-author">{comment.user_name}</span>
                  <span className="comment-time">{formatTimestamp(comment.created_at)}</span>
                </div>
                
                {comment.text && comment.text.trim() && (
                  <div className="comment-text">
                    {extractLinks(comment.text).map((part, idx) => (
                      part.type === 'link' ? (
                        <a 
                          key={idx} 
                          href={part.url || part.content} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="comment-link"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {part.content}
                        </a>
                      ) : (
                        <span key={idx} style={{ whiteSpace: 'pre-wrap' }}>{part.content}</span>
                      )
                    ))}
                  </div>
                )}
                
                {comment.image_url && (
                  <div className="comment-image">
                    <img src={comment.image_url} alt="Attachment" />
                  </div>
                )}
                
                {comment.user_id === currentUser.id && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="delete-comment-btn"
                    onClick={() => handleDeleteComment(comment.id)}
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="comment-input-section">
        {imagePreview && (
          <div className="image-preview">
            <img src={imagePreview} alt="Preview" />
            <Button
              variant="ghost"
              size="sm"
              className="remove-image-btn"
              onClick={() => {
                setImageFile(null);
                setImagePreview(null);
              }}
            >
              ×
            </Button>
          </div>
        )}
        
        {showLinkInput && (
          <div className="link-input-container">
            <Input
              type="url"
              placeholder="Paste link URL..."
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              className="link-input"
            />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setLinkUrl('');
                setShowLinkInput(false);
              }}
            >
              ×
            </Button>
          </div>
        )}
        
        <div className="comment-input-box">
          <Textarea
            placeholder="Add a comment..."
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendComment();
              }
            }}
            className="comment-textarea"
            rows={3}
          />
          
          <div className="comment-actions">
            <div className="comment-attachments">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                style={{ display: 'none' }}
              />
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                title="Upload image (max 1MB)"
              >
                <ImageIcon size={18} />
              </Button>
              
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowLinkInput(!showLinkInput)}
                title="Add link"
              >
                <LinkIcon size={18} />
              </Button>
            </div>
            
            <Button
              onClick={handleSendComment}
              disabled={isSending || (!newComment.trim() && !imageFile && !linkUrl)}
              size="sm"
            >
              {isSending ? (
                <Loader2 className="animate-spin" size={18} />
              ) : (
                <>
                  <Send size={18} />
                  <span>Send</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
